import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshMe = useCallback(async () => {
        const token = localStorage.getItem("sdc_token");
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            localStorage.setItem("sdc_user", JSON.stringify(data));
        } catch {
            localStorage.removeItem("sdc_token");
            localStorage.removeItem("sdc_user");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshMe();
    }, [refreshMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("sdc_token", data.token);
        localStorage.setItem("sdc_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const setup = async ({ name, email, password }) => {
        const { data } = await api.post("/auth/setup", { name, email, password });
        localStorage.setItem("sdc_token", data.token);
        localStorage.setItem("sdc_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Server-side logout is best-effort; client-side cleanup must still proceed.
            console.warn("Logout API call failed, clearing client session anyway", e);
        }
        localStorage.removeItem("sdc_token");
        localStorage.removeItem("sdc_user");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, setup, logout, refreshMe, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider/>");
    return ctx;
}
